import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { childPath, isBlank, isBranch, isEmpty } from "@/lib/json";
import { JsonLeaf, RowActions } from "./json-leaf";

export interface TreeOptions {
  onPick?: ((path: string) => void) | undefined;
  /** Prefix for every path, e.g. "output" when the tree shows only the answer. */
  root: string;
  hideEmpty: boolean;
  /** Levels open at first. */
  openDepth: number;
}

const entriesOf = (value: Record<string, unknown> | unknown[], hideEmpty: boolean) =>
  Object.entries(value).filter(([, v]) => !hideEmpty || !isBlank(v));

function Row({
  name,
  path,
  pickable,
  inArray,
  value,
  depth,
  opts,
}: {
  name: string;
  path: string;
  pickable: boolean;
  inArray: boolean;
  value: unknown;
  depth: number;
  opts: TreeOptions;
}) {
  const [open, setOpen] = useState(depth < opts.openDepth);
  const branch = isBranch(value) && !isEmpty(value);
  const array = Array.isArray(value);
  const count = branch ? Object.keys(value).length : 0;
  const pick = pickable && opts.onPick ? () => opts.onPick?.(path) : null;
  const keyClass = `shrink-0 whitespace-nowrap ${inArray ? "text-faint" : "font-medium text-ink"}`;
  return (
    <li className="flex min-w-0 flex-col">
      <div className="group flex min-w-0 items-start gap-1 rounded-[4px] py-px pr-1 hover:bg-hover">
        {branch ? (
          <button
            type="button"
            className="mt-[3px] shrink-0 text-faint hover:text-ink"
            aria-label={`${open ? "Collapse" : "Expand"} ${path}`}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <ChevronRight size={12} className={`transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
        ) : (
          <span className="w-3 shrink-0" />
        )}
        {pick ? (
          <button
            type="button"
            title={`Use ${path}`}
            className={`${keyClass} rounded-[3px] text-link hover:bg-run-bg`}
            onClick={pick}
          >
            {name}
          </button>
        ) : (
          <span className={keyClass}>{name}</span>
        )}
        <span className="shrink-0 text-faint">:</span>
        <span className="min-w-0 flex-1">
          {branch ? (
            !open && (
              <span className="text-faint">
                {array ? `[ ${count} ]` : `{ ${count} ${count === 1 ? "key" : "keys"} }`}
              </span>
            )
          ) : (
            <JsonLeaf value={value} />
          )}
        </span>
        <RowActions path={path} value={value} onPick={pick} />
      </div>
      {branch && open && (
        <Children value={value} path={path} pickable={pickable} depth={depth + 1} opts={opts} />
      )}
    </li>
  );
}

function Children({
  value,
  path,
  pickable,
  depth,
  opts,
  label,
}: {
  value: Record<string, unknown> | unknown[];
  path: string;
  pickable: boolean;
  depth: number;
  opts: TreeOptions;
  label?: string;
}) {
  const array = Array.isArray(value);
  return (
    <ul aria-label={label} className={depth > 0 ? "ml-[5px] border-l border-line-soft pl-2.5" : ""}>
      {entriesOf(value, opts.hideEmpty).map(([k, v]) => (
        <Row
          key={k}
          name={k}
          path={childPath(path, k, array)}
          pickable={pickable}
          inArray={array}
          value={v}
          depth={depth}
          opts={opts}
        />
      ))}
    </ul>
  );
}

export function JsonTree({
  value,
  label,
  opts,
}: {
  value: Record<string, unknown> | unknown[];
  label: string;
  opts: TreeOptions;
}) {
  return (
    <div className="font-mono text-xs leading-relaxed">
      <Children value={value} path={opts.root} pickable depth={0} opts={opts} label={label} />
    </div>
  );
}
